import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/api';
import { LeadSubStage } from '../../types/lead';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const MAIN_STATUSES = [
  'interested', 'not_interested', 'follow_up', 'app_out', 'app_in', 'unqualified',
];

const AdminSubStageConfigPage: React.FC = () => {
  const [mainStatus, setMainStatus] = useState('interested');
  const [grouped, setGrouped] = useState<Record<string, LeadSubStage[]>>({});
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/lead-sub-stages');
      setGrouped(res.data.data.grouped || {});
    } catch {
      toast.error('Failed to load sub-stages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const stages = grouped[mainStatus] || [];

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await apiClient.post('/admin/lead-sub-stages', {
        mainStatus,
        name: name.trim(),
        allowsFollowUp: mainStatus === 'follow_up' || mainStatus === 'interested',
      });
      setName('');
      await load();
      toast.success('Sub-stage created');
    } catch {
      toast.error('Failed to create sub-stage');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await apiClient.delete(`/admin/lead-sub-stages/${id}`);
      await load();
      toast.success('Sub-stage removed');
    } catch {
      toast.error('Failed to remove sub-stage');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Lead Sub-Stages</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure sub-stages for each main status. Agents will see these in results and in-call popup.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Main Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <select className="input max-w-xs" value={mainStatus} onChange={(e) => setMainStatus(e.target.value)}>
            {MAIN_STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>

          <div className="flex gap-2 max-w-lg">
            <input
              className="input flex-1"
              placeholder="New sub-stage name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button onClick={handleCreate} disabled={saving || !name.trim()}>Add</Button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : stages.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sub-stages for this status yet.</p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg">
              {stages.map((s) => (
                <li key={s.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span>{s.name}</span>
                  <Button variant="outlined" size="sm" onClick={() => handleDeactivate(s.id)}>Remove</Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSubStageConfigPage;
